import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'

import { ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { IActionRepeaterValue, parseUrlToMakyoImageSize, TypographyGroupValue } from '@makyoapp/makyo-common'
import {
  ColorVariant,
  Comment,
  CommentUser,
  HoverEffectVariant,
  HoverIntensity,
  parseSpacing,
  RawSpacingInput,
  safeAdjustColor,
  SingleComment,
  Skeleton,
  SkeletonGroup,
  spacingToStyles,
  TypographyValue,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey } from '@/shared/builder/types'
import { THUMB_AVATAR } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompMenuV2 } from '../menu-v2/comp'

function sortComments(comments: SingleComment[], sort: 'newest' | 'popular' | 'oldest') {
  return comments.sort((a, b) => {
    switch (sort) {
      case 'newest':
        return b.timeStamp - a.timeStamp
      case 'oldest':
        return a.timeStamp - b.timeStamp
      case 'popular':
        return b.likes - a.likes
      default:
        return 0
    }
  })
}

function generateCommentData({
  allRows,
  parentColumnId,
  createCommentItem,
}: {
  allRows: IRow[]
  parentColumnId: string
  createCommentItem: (row: IRow, parentId?: string) => SingleComment
}) {
  // Create a map of all comments by ID for O(1) lookup
  const commentMap = new Map<string, SingleComment>()
  const rootComments: SingleComment[] = []

  // First pass: create all comment items
  allRows.forEach((row) => {
    const comment = createCommentItem(row)
    commentMap.set(comment.id, comment)
  })

  // Second pass: build the tree structure
  allRows.forEach((row) => {
    const comment = commentMap.get(row._id)!
    const parentId = row[parentColumnId]?.value?.toString()

    if (isEmpty(row[parentColumnId]?.value)) {
      // Top-level comment
      rootComments.push(comment)
    } else {
      // This is a reply - find its parent and attach it
      const parentComment = parentId ? commentMap.get(parentId) : null
      if (parentComment) {
        comment.parentId = parentId
        parentComment.replies = [...(parentComment.replies || []), comment]
      } else {
        rootComments.push(comment)
      }
    }
  })

  return rootComments
}

export interface CompCommentWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

/**
 * Comment Wrapper Core
 */
export function CompCommentWrapperCore({
  infos: { component, settings, currentParentBg = 'primary-95', detailTableId, detailRowIndex, rowId: detailRowId },
  action: { simulateAction },
  tableData: { language, userSpecific, dbTable: table },
}: CompCommentWrapperCoreProps) {
  const { currentUser, tableUsers } = useAppAuthContext()
  const { menus, isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  const users = useMemo(() => {
    return tableUsers.map((user) => ({
      id: user.id as any,
      name: user.name,
      avatar: user.photo || THUMB_AVATAR,
      username: user.email,
    }))
  }, [tableUsers])

  const author = useMemo(() => {
    return {
      id: currentUser?.id as any,
      name: currentUser?.name ?? '',
      avatar: currentUser?.photo || THUMB_AVATAR,
      username: currentUser?.email ?? '',
    } as CommentUser
  }, [currentUser])

  // ========================= data =========================
  // data source
  const tableId = settings[ComponentSettingKey.CommentDataSource] as string

  // columns
  const colComment = settings[ComponentSettingKey.CommentDataComment] as string
  const colAuthor = settings[ComponentSettingKey.CommentDataAuthor] as string
  const colAvatar = settings[ComponentSettingKey.CommentDataAvatar] as string
  const colParent = settings[ComponentSettingKey.CommentDataParent] as string
  const colUser = settings[ComponentSettingKey.CommentDataUser] as string
  const colDate = settings[ComponentSettingKey.CommentDataDate] as string
  const colLikes = settings[ComponentSettingKey.CommentDataLikes] as string
  const colDislikes = settings[ComponentSettingKey.CommentDataDislikes] as string

  // fetch data config
  const filterSetting = settings[ComponentSettingKey.CommentDataFilter]
  const sortBy = settings[ComponentSettingKey.CommentDataSortBy] as string
  const sortType = settings[ComponentSettingKey.CommentDataSortType] as string

  // dataset limit
  const datasetLimit = settings[ComponentSettingKey.CommentDataLimit]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : 10000

  // fetch all rows
  const [{ rows, filteredRowsCount, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    filterSetting,
    sortSetting: { sortType, sortBy },
    pagination: { page: 0, perPage: maxPerItems },
  })

  // todo: use state instead
  const rowIndexes = useMemo(() => {
    return table.GetRowIndexesMap(language, userSpecific)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, language, userSpecific, filteredRowsCount]) // allRowsCount is needed to trigger re-render

  // local state
  const [commentSortBy, setCommentSortBy] = useState<'newest' | 'popular' | 'oldest'>('newest')

  const commentsData = useMemo(() => {
    const comments = generateCommentData({
      allRows: rows,
      parentColumnId: colParent,
      createCommentItem: (row, parentId) => ({
        id: row._id,
        author: row[colAuthor]?.toString() ?? '',
        avatar: parseUrlToMakyoImageSize(row[colAvatar]?.toString() ?? '', ImageMetadata.XS),
        content: row[colComment]?.toString() ?? '',
        timeStamp: new Date(row[colDate]?.value as any).getTime(),
        likes: (row[colLikes]?.value as any) ?? 0,
        dislikes: (row[colDislikes]?.value as any) ?? 0,
        replies: [],
      }),
    })
    return sortComments(comments, commentSortBy)
  }, [rows, colParent, commentSortBy, colAuthor, colAvatar, colComment, colDate, colLikes, colDislikes])

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.CommentDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.CommentDesignCompHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.CommentDesignMargin] as RawSpacingInput

  // styles
  // todo
  const itemGap = settings[ComponentSettingKey.CommentDesignItemGap] as RawSpacingInput

  const hoverEffect = settings[ComponentSettingKey.CommentDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  // typography
  const typoSettings = settings[ComponentSettingKey.CommentDesignTypography] as TypographyGroupValue
  const nameTypography = typoSettings[ComponentSettingKey.CommentDesignAuthorTypography] as TypographyValue
  const contentTypography = typoSettings[ComponentSettingKey.CommentDesignBodyTypography] as TypographyValue
  const replyTypography = typoSettings[ComponentSettingKey.CommentDesignReplyButtonTypography] as TypographyValue

  // color
  const commentColor = settings[ComponentSettingKey.CommentDesignCommentColor]
  const authorNameColor = settings[ComponentSettingKey.CommentDesignAuthorNameColor]
  const authorAvatarColor = settings[ComponentSettingKey.CommentDesignAuthorAvatarColor]
  const timeColor = settings[ComponentSettingKey.CommentDesignTimeColor]
  const likeColor = settings[ComponentSettingKey.CommentDesignLikeColor]
  const dislikeColor = settings[ComponentSettingKey.CommentDesignDislikeColor]

  const commentColorSolid = settings[ComponentSettingKey.CommentDesignCommentColorSolid] || 'primary-40'
  const authorNameColorSolid = settings[ComponentSettingKey.CommentDesignAuthorNameColorSolid] || 'primary-40'
  const timeColorSolid = settings[ComponentSettingKey.CommentDesignTimeColorSolid] || 'primary-40'
  const likeColorSolid = settings[ComponentSettingKey.CommentDesignLikeColorSolid] || 'primary-40'
  const dislikeColorSolid = settings[ComponentSettingKey.CommentDesignDislikeColorSolid] || 'primary-40'

  const commentColorStartGradient = settings[ComponentSettingKey.CommentDesignCommentColorStartGradient] || 'primary-40'
  const authorNameColorStartGradient =
    settings[ComponentSettingKey.CommentDesignAuthorNameColorStartGradient] || 'primary-40'
  const authorAvatarColorStartGradient =
    settings[ComponentSettingKey.CommentDesignAuthorAvatarColorStartGradient] || 'primary-40'
  const timeColorStartGradient = settings[ComponentSettingKey.CommentDesignTimeColorStartGradient] || 'primary-40'
  const likeColorStartGradient = settings[ComponentSettingKey.CommentDesignLikeColorStartGradient] || 'primary-40'
  const dislikeColorStartGradient = settings[ComponentSettingKey.CommentDesignDislikeColorStartGradient] || 'primary-40'

  const commentColorEndGradient = settings[ComponentSettingKey.CommentDesignCommentColorEndGradient] || 'primary-40'
  const commentColorGradientDirection =
    settings[ComponentSettingKey.CommentDesignCommentColorGradientDirection] || 'primary-40'
  const authorNameColorEndGradient =
    settings[ComponentSettingKey.CommentDesignAuthorNameColorEndGradient] || 'primary-40'
  const authorAvatarColorEndGradient =
    settings[ComponentSettingKey.CommentDesignAuthorAvatarColorEndGradient] || 'primary-40'
  const timeColorEndGradient = settings[ComponentSettingKey.CommentDesignTimeColorEndGradient] || 'primary-40'
  const likeColorEndGradient = settings[ComponentSettingKey.CommentDesignLikeColorEndGradient] || 'primary-40'
  const dislikeColorEndGradient = settings[ComponentSettingKey.CommentDesignDislikeColorEndGradient] || 'primary-40'

  const authorNameColorGradientDirection = settings[ComponentSettingKey.CommentDesignAuthorNameColorGradientDirection]
  const authorAvatarColorGradientDirection =
    settings[ComponentSettingKey.CommentDesignAuthorAvatarColorGradientDirection]
  const timeColorGradientDirection = settings[ComponentSettingKey.CommentDesignTimeColorGradientDirection]
  const likeColorGradientDirection = settings[ComponentSettingKey.CommentDesignLikeColorGradientDirection]
  const dislikeColorGradientDirection = settings[ComponentSettingKey.CommentDesignDislikeColorGradientDirection]

  // ========================= others =========================

  // menu
  const menuId = settings[ComponentSettingKey.CommentDataActionMenu] as string
  const menu = menus.find(({ id }) => id === menuId)

  // ========================= action =========================
  const onCommentAction = settings[ComponentSettingKey.CommentActionCommentSubmit] as IActionRepeaterValue[]
  const onReplyAction = settings[ComponentSettingKey.CommentActionReplySubmit] as IActionRepeaterValue[]
  const onAuthorClickAction = settings[ComponentSettingKey.CommentActionAuthorClick] as IActionRepeaterValue[]
  const onLikeClickAction = settings[ComponentSettingKey.CommentActionLikeClick] as IActionRepeaterValue[]
  const onDislikeClickAction = settings[ComponentSettingKey.CommentActionDislikeClick] as IActionRepeaterValue[]

  const handleNewComment = (text: string) => {
    simulateAction({
      actions: onCommentAction,
      external: {
        detailsPage: {
          detailTableId,
          detailRowIndex,
          detailRowId,
        },
        comment: {
          comment: text,
        },
      },
    })
  }

  const handleReply = (text: string, parentId?: string) => {
    if (!parentId) return

    simulateAction({
      actions: onReplyAction,
      external: {
        detailsPage: {
          detailTableId,
          detailRowIndex,
          detailRowId,
        },
        comment: {
          comment: text,
          parentId,
        },
      },
    })
  }

  const handleLike = (commentId: string) => {
    simulateAction({
      actions: onLikeClickAction,
      tableId,
      rowIndex: rowIndexes.get(commentId) as number,
      rowId: commentId,
      external: {
        detailsPage: {
          detailTableId,
          detailRowIndex,
          detailRowId,
        },
      },
    })
  }

  const handleDislike = (commentId: string) => {
    simulateAction({
      actions: onDislikeClickAction,
      tableId,
      rowIndex: rowIndexes.get(commentId) as number,
      rowId: commentId,
      external: {
        detailsPage: {
          detailTableId,
          detailRowIndex,
          detailRowId,
        },
      },
    })
  }

  const handleAuthorClick = (commentId: string) => {
    const row = rows.find((row) => row._id === commentId)

    // find user id from userIdOrEmail in tableUsers
    const userIdOrEmail = row?.[colUser]?.value?.toString()
    const userId = tableUsers.find((user) => user.id === userIdOrEmail || user.email === userIdOrEmail)?.id

    simulateAction({
      actions: onAuthorClickAction,
      tableId,
      external: {
        detailsPage: {
          detailTableId,
          detailRowIndex,
          detailRowId,
        },
        comment: {
          userId,
        },
      },
    })
  }

  // ========================= behavior =========================
  const showLikeCount = settings[ComponentSettingKey.CommentBehaviorShowLikeCount] ?? true
  const showDislikeCount = settings[ComponentSettingKey.CommentBehaviorShowDislikeCount] ?? true

  // ========================= render =========================
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  if (isLoading) {
    return <CommentSkeleton backgroundColor={currentParentBg!} margin={margin} />
  }

  const commentComponent = (
    <Comment
      id={component._id}
      width={width}
      alignment={alignment}
      backgroundColor={currentParentBg}
      margin={margin}
      // core data
      comments={commentsData}
      users={users}
      avatar={author.avatar}
      // typography
      nameTypography={nameTypography}
      contentTypography={contentTypography}
      replyTypography={replyTypography}
      // behavior
      showLikeCount={showLikeCount}
      showDislikeCount={showDislikeCount}
      // callbacks
      onNewComment={handleNewComment}
      onReply={handleReply}
      onSortChange={setCommentSortBy}
      onLike={handleLike}
      onDislike={handleDislike}
      onAuthorClick={handleAuthorClick}
      // format timestamp: relative for < 1 week, absolute for older
      formatTimeStamp={(timeStamp) => {
        const now = dayjs()
        const commentDate = dayjs(timeStamp)
        const daysDiff = now.diff(commentDate, 'day')

        if (daysDiff < 7) {
          // Show relative time for dates less than a week old
          return commentDate.fromNow()
        } else {
          // Show absolute date for older comments
          return commentDate.format('MMM D, YYYY')
        }
      }}
      renderActionMenu={(commentId) => {
        return (
          <CompMenuV2
            key={commentId}
            menuId={menuId}
            backgroundColor={safeAdjustColor(currentParentBg, -20)}
            triggerButtonColor={currentParentBg}
            tableContext={{
              tableId,
              rowId: commentId,
              rowIndex: rowIndexes.get(commentId) as number,
            }}
          />
        )
      }}
      commentColorType={commentColor}
      authorNameColorType={authorNameColor}
      authorAvatarColorType={authorAvatarColor}
      timeColorType={timeColor}
      likeColorType={likeColor}
      dislikeColorType={dislikeColor}
      commentColorSolid={commentColorSolid}
      authorNameColorSolid={authorNameColorSolid}
      timeColorSolid={timeColorSolid}
      likeColorSolid={likeColorSolid}
      dislikeColorSolid={dislikeColorSolid}
      commentColorStartGradient={commentColorStartGradient}
      authorNameColorStartGradient={authorNameColorStartGradient}
      authorAvatarColorStartGradient={authorAvatarColorStartGradient}
      timeColorStartGradient={timeColorStartGradient}
      likeColorStartGradient={likeColorStartGradient}
      dislikeColorStartGradient={dislikeColorStartGradient}
      commentColorEndGradient={commentColorEndGradient}
      authorNameColorEndGradient={authorNameColorEndGradient}
      authorAvatarColorEndGradient={authorAvatarColorEndGradient}
      timeColorEndGradient={timeColorEndGradient}
      likeColorEndGradient={likeColorEndGradient}
      dislikeColorEndGradient={dislikeColorEndGradient}
      commentColorGradientDirection={commentColorGradientDirection}
      authorNameColorGradientDirection={authorNameColorGradientDirection}
      authorAvatarColorGradientDirection={authorAvatarColorGradientDirection}
      timeColorGradientDirection={timeColorGradientDirection}
      likeColorGradientDirection={likeColorGradientDirection}
      dislikeColorGradientDirection={dislikeColorGradientDirection}
      hoverEffect={hoverEffect as HoverEffectVariant}
      hoverIntensity={hoverIntensity as HoverIntensity}
    />
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {commentComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return commentComponent
}

const CommentSkeleton = ({ backgroundColor, margin }: { backgroundColor: ColorVariant; margin: RawSpacingInput }) => {
  return (
    <SkeletonGroup backgroundColor={backgroundColor}>
      <div
        className='w-full'
        style={{
          ...spacingToStyles(parseSpacing(margin), 'padding'),
        }}
      >
        <div className='py-4'>
          <div className='flex items-center justify-between'>
            <Skeleton className='w-[100px] h-4 rounded-md' />
            <Skeleton className='w-[100px] h-4 rounded-md' />
          </div>

          <div className='pt-4 w-full flex gap-4'>
            <Skeleton className='w-10 h-10 aspect-square rounded-full shrink-0' />
            <div className='flex-1 space-y-2'>
              <Skeleton className='w-full h-10 rounded-md' />
              <Skeleton className='w-full h-2 rounded-md' />
            </div>
          </div>
        </div>

        {[...new Array(2)].map((_, i) => (
          <div key={i} className='pl-8 py-6 w-full max-w-full flex gap-4'>
            <Skeleton className='w-10 h-10 aspect-square rounded-full shrink-0' />
            <div className='space-y-2 flex-1'>
              <Skeleton className='w-20 h-3 rounded-md' />
              <Skeleton className='w-full h-3 rounded-md' />
              <Skeleton className='w-full h-3 rounded-md' />
            </div>
          </div>
        ))}
      </div>
    </SkeletonGroup>
  )
}
